import type { Module } from 'vuex'

import { getUserInfo, loginByUsername, logout } from '@/api/login'
import { identifyUser, resetUser } from '@/telemetry/posthogTelemetry'
import type { LoginCredentials, SessionTokens, StoredAuthData } from '@/types/session'
import type { UserRole } from '@/types/UserRoles'
import { getAuthData, getToken, removeAuthData, removeToken, setAuthData, setToken } from '@/utils/auth'

export interface UserState {
  status: boolean | ''
  token: string | undefined
  name: string | undefined
  roles: UserRole[]
  notificationChannels: string[]
  authData: SessionTokens | StoredAuthData | Record<string, never>
}

interface UserMutationPayloads {
  SET_TOKEN: UserState['token']
  SET_STATUS: UserState['status']
  SET_NAME: UserState['name']
  SET_ROLES: UserState['roles']
  SET_AUTH_DATA: UserState['authData']
  SET_NOTIFICATION_CHANNELS: UserState['notificationChannels']
}

interface UserActionContext {
  commit: <Mutation extends keyof UserMutationPayloads>(type: Mutation, payload: UserMutationPayloads[Mutation]) => void
  state: UserState
}

const state: UserState = {
  status: '',
  token: getToken(),
  name: '',
  roles: [],
  notificationChannels: [],
  authData: getAuthData()
}

const user = {
  state,

  mutations: {
    SET_TOKEN: (state: UserState, token: UserState['token']) => {
      state.token = token
    },
    SET_STATUS: (state: UserState, status: UserState['status']) => {
      state.status = status
    },
    SET_NAME: (state: UserState, name: UserState['name']) => {
      state.name = name
    },
    SET_ROLES: (state: UserState, roles: UserState['roles']) => {
      state.roles = roles
    },
    SET_AUTH_DATA: (state: UserState, authData: UserState['authData']) => {
      state.authData = authData
    },
    SET_NOTIFICATION_CHANNELS: (state: UserState, notificationChannels: UserState['notificationChannels']) => {
      state.notificationChannels = notificationChannels
    }
  },

  actions: {
    // User name login
    LoginByUsername({ commit }: UserActionContext, userInfo: LoginCredentials) {
      const username = userInfo.username.trim()
      return new Promise<void>((resolve, reject) => {
        loginByUsername(username, userInfo.password)
          .then(response => {
            commit('SET_TOKEN', response.data.accessToken)
            commit('SET_AUTH_DATA', response.data)
            setToken(response.data.accessToken)
            setAuthData(response.data)
            identifyUser({ username })
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    // Get user information
    GetUserInfo({ commit, state }: UserActionContext) {
      return new Promise<Awaited<ReturnType<typeof getUserInfo>>>((resolve, reject) => {
        getUserInfo(state.token)
          .then(response => {
            if (!response) {
              // Since mockjs doesn't support custom status codes this can only be hacked
              reject('error')
            }
            commit('SET_ROLES', response.data.roles)
            commit('SET_NOTIFICATION_CHANNELS', response.data.notificationChannels ?? [])
            commit('SET_NAME', response.data.username)
            commit('SET_STATUS', response.data.enabled)
            identifyUser({
              username: response.data.username,
              roles: response.data.roles
            })
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    // Sign out
    LogOut({ commit, state }: UserActionContext) {
      return new Promise<void>((resolve, reject) => {
        logout(state.token)
          .then(() => {
            commit('SET_TOKEN', '')
            commit('SET_ROLES', [])
            commit('SET_NOTIFICATION_CHANNELS', [])
            commit('SET_AUTH_DATA', {})
            removeToken()
            removeAuthData()
            resetUser()
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    FedLogOut({ commit }: UserActionContext) {
      return new Promise<void>(resolve => {
        commit('SET_TOKEN', '')
        commit('SET_ROLES', [])
        commit('SET_NOTIFICATION_CHANNELS', [])
        commit('SET_AUTH_DATA', {})
        removeToken()
        removeAuthData()
        resetUser()
        resolve()
      })
    },

    // Dynamically modify permissions
    ChangeRoles({ commit }: UserActionContext, role: string) {
      return new Promise<void>(resolve => {
        commit('SET_TOKEN', role)
        setToken(role)
        getUserInfo(role).then(response => {
          const data = response.data
          commit('SET_ROLES', data.roles)
          commit('SET_NAME', data.name)
          resolve()
        })
      })
    },

    ChangeStatus({ commit }: UserActionContext, status: UserState['status']) {
      return new Promise<void>((resolve, reject) => {
        commit('SET_STATUS', status)
        getUserInfo()
          .then(response => {
            if (!response) {
              reject('error')
            }
            commit('SET_STATUS', response.data.enabled)
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    SetAuthData({ commit }: UserActionContext, authData: SessionTokens) {
      return new Promise<void>(resolve => {
        commit('SET_AUTH_DATA', authData)
        commit('SET_TOKEN', authData.accessToken)
        setToken(authData.accessToken)
        resolve()
      })
    }
  }
} satisfies Module<UserState, unknown>

export default user
